import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Flex, 
  Badge, 
  IconButton, 
  Spinner, 
  Center, 
  useColorModeValue, 
  useBreakpointValue,
  useToast, 
  Avatar, 
  Divider,
  SimpleGrid,
  Image,
  Container,
  Heading,
  Stack,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spacer,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { 
  FiPlay, 
  FiPlus, 
  FiThumbsUp, 
  FiThumbsDown, 
  FiShare, 
  FiHeart, 
  FiArrowLeft,
  FiInfo,
  FiList,
  FiDownload,
  FiBookmark,
  FiEdit,
  FiFlag,
  FiUser
} from 'react-icons/fi';
import { 
  FaThumbsUp, 
  FaThumbsDown, 
  FaBookmark as FaBookmarkSolid,
  FaHeart as FaHeartSolid
} from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';
import CommentSection from '../components/CommentSection';
import { getContentById, getEpisodesForSeries, getSeasonsForSeries, type ContentItem } from '../services/contentService';
// import { recordView } from '../services/analyticsService';
import { useAuth } from '../contexts/AuthContext';
import { toggleWatchlist, isInWatchlist } from '../services/watchlistService';
import { shareContent, generateShareLink } from '../services/shareService';
import { toggleLike, getUserLike, getContentLikes } from '../services/likesService';
import { getUserProfile, type UserProfile } from '../services/userService';
import { getSubscriptionCount, isFollowing, followCreator, unfollowCreator } from '../services/subscriptionsService';
import { deleteContent } from '../services/contentDeletionService';
import EditContentModal from '../components/EditContentModal';
import EditBookModal from '../components/EditBookModal';
import EditEpisodeModal from '../components/EditEpisodeModal';
import ReportContentModal from '../components/ReportContentModal';
import ReportUserModal from '../components/ReportUserModal';
import DMCARequestModal from '../components/DMCARequestModal';
import DonationPaywallModal from '../components/DonationPaywallModal';
import AdSenseSlot from '../components/AdSenseSlot';
import { ADSENSE_CLIENT_ID, ADSENSE_SLOTS } from '../constants/adsense';
import { useAdFree } from '../hooks/useAdFree';
import SubscriptionService from '../services/subscriptionService';

const ContentDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { isAdFree } = useAdFree();
  const watchStartRef = useRef<number | null>(null);
  const playIconSize = useBreakpointValue({ base: 18, md: 20 }) || 20;
  
  // Content state
  const [content, setContent] = useState<ContentItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Like/Dislike state
  const [userRating, setUserRating] = useState<'like' | 'dislike' | null>(null);
  const [likeCounts, setLikeCounts] = useState<{ likes: number; dislikes: number }>({ likes: 0, dislikes: 0 });
  
  // Creator state
  const [creatorProfile, setCreatorProfile] = useState<UserProfile | null>(null);
  const [subscriberCount, setSubscriberCount] = useState<number>(0);
  const [isSubscribedToCreator, setIsSubscribedToCreator] = useState<boolean>(false);
  
  // Bookmark state
  const [isBookmarked, setIsBookmarked] = useState(false);
  
  // Donation modal state
  
  // Delete operation state
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Episode edit modal state
  const [isEpisodeEditModalOpen, setIsEpisodeEditModalOpen] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState<ContentItem | null>(null);
  
  // Report modal states
  const [isReportContentModalOpen, setIsReportContentModalOpen] = useState(false);
  const [isReportUserModalOpen, setIsReportUserModalOpen] = useState(false);
  const [isDMCARequestModalOpen, setIsDMCARequestModalOpen] = useState(false);
  
  // Donation paywall modal state
  const [isDonationPaywallOpen, setIsDonationPaywallOpen] = useState(false);
  const [hasDonated, setHasDonated] = useState(false);
  
  // Delete confirmation dialog state
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  
  // Episodes state
  const [episodes, setEpisodes] = useState<ContentItem[]>([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);
  
  // Seasons state
  const [seasons, setSeasons] = useState<{ seasonNumber: string; episodeCount: number; episodes: ContentItem[] }[]>([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  // Fetch episodes function
  const fetchEpisodes = async () => {
    if (!id || content?.category !== 'series') return;
    
    setLoadingEpisodes(true);
    setLoadingSeasons(true);
    try {
      // Check if series has seasons data stored directly in the document
      // Check both possible locations for seasons data
      const seasonsData = (content as any).seasons || content.assets?.seasons;
      if (content && seasonsData && Array.isArray(seasonsData) && seasonsData.length > 0) {
        const allEpisodes: ContentItem[] = [];
        const seasonsList: { seasonNumber: string; episodeCount: number; episodes: ContentItem[] }[] = [];
        
        seasonsData.forEach((season: any, seasonIndex: number) => {
          if (season.episodes && Array.isArray(season.episodes) && season.episodes.length > 0) {
            const seasonEpisodes: ContentItem[] = season.episodes.map((episode: any, episodeIndex: number) => ({
              id: episode.id || `${id}-s${seasonIndex + 1}-e${episodeIndex + 1}`,
              title: episode.title || `Episode ${episodeIndex + 1}`,
              description: episode.description || '',
              category: 'episode',
              thumbnail: episode.thumbnail || content.thumbnail || content.poster || '',
              assets: {
                video: episode.videoUrl || episode.video || episode.videoFile,
                videoFile: episode.videoUrl || episode.video || episode.videoFile,
                videoUrl: episode.videoUrl || episode.video || episode.videoFile
              },
              seasonNumber: season.seasonNumber || (seasonIndex + 1).toString(),
              episodeNumber: episode.episodeNumber || (episodeIndex + 1).toString(),
              uploader: content.uploader,
              uploaderId: content.uploaderId,
              duration: episode.duration || 45,
              views: episode.views || 0,
              likes: episode.likes || 0,
              dislikes: episode.dislikes || 0,
              createdAt: episode.createdAt || content.createdAt,
              updatedAt: episode.updatedAt || content.updatedAt
            }));
            
            allEpisodes.push(...seasonEpisodes);
            seasonsList.push({
              seasonNumber: season.seasonNumber || (seasonIndex + 1).toString(),
              episodeCount: seasonEpisodes.length,
              episodes: seasonEpisodes
            });
          }
        });
        
        setEpisodes(allEpisodes);
        setSeasons(seasonsList);
      } else {
        // Fallback to old method for backward compatibility - fetch episodes from separate documents
        const fetchedEpisodes = await getEpisodesForSeries(id);
        setEpisodes(fetchedEpisodes);
        
        const fetchedSeasons = await getSeasonsForSeries(id);
        setSeasons(fetchedSeasons);
      }
    } catch (error) {
      console.error('Error fetching episodes:', error);
      toast({
        title: 'Error loading seasons',
        description: 'Failed to load series seasons. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingEpisodes(false);
      setLoadingSeasons(false);
    }
  };

  // Fetch content data
  useEffect(() => {
    const fetchContent = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const contentData = await getContentById(id);
        setContent(contentData);
        
        // Redirect shorts to ShortPlayer
        if (contentData && contentData.category === 'short') {
          navigate(`/short/${id}`, { replace: true });
          return;
        }
        
        // Redirect books to BookDetail
        if (contentData && contentData.category === 'book') {
          navigate(`/book/${id}`, { replace: true });
          return;
        }
        
        if (contentData) {
        // Record view
          // await recordView(id, contentData.uploaderId); // Commented out - function not available
          
          // Fetch creator profile
          try {
            const profile = await getUserProfile(contentData.uploaderId);
        setCreatorProfile(profile);
          } catch (error) {
            console.error('Error fetching creator profile:', error);
          }
          
          // Fetch subscriber count
          try {
            const count = await getSubscriptionCount(contentData.uploaderId);
        setSubscriberCount(count);
          } catch (error) {
            console.error('Error fetching subscriber count:', error);
          }
          
          // Check if user is following
          if (user) {
            try {
              const following = await isFollowing(user.uid, contentData.uploaderId);
        setIsSubscribedToCreator(following);
    } catch (error) {
              console.error('Error checking subscription:', error);
            }
          }
          
          // Fetch like counts
          try {
            const likes = await getContentLikes(id);
            setLikeCounts(likes);
          } catch (error) {
            console.error('Error fetching likes:', error);
          }
          
          // Check user's rating
          if (user) {
            try {
              const rating = await getUserLike(user.uid, id);
              setUserRating(rating);
            } catch (error) {
              console.error('Error fetching user rating:', error);
            }
          }
          
           // Check if bookmarked
           if (user) {
             try {
               const bookmarked = await isInWatchlist(user.uid, id);
               setIsBookmarked(bookmarked);
    } catch (error) {
               console.error('Error checking bookmark:', error);
             }
           }
           
          // Fetch episodes and seasons if it's a series
          if (contentData.category === 'series') {
            await fetchEpisodes();
          }
         }
    } catch (error) {
        console.error('Error fetching content:', error);
        setError('Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [id, user]);

  const handleBookmark = async () => {
    if (!user || !id) {
      toast({ title: 'Please sign in', status: 'warning' });
      return;
    }

    try {
      await toggleWatchlist(user.uid, id);
      setIsBookmarked(!isBookmarked);
        toast({
        title: isBookmarked ? 'Removed from watchlist' : 'Added to watchlist',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
    } catch (error) {
      console.error('Bookmark error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update watchlist. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleLike = async (action: 'like' | 'dislike') => {
    if (!user || !id) {
      toast({ title: 'Please sign in', status: 'warning' });
      return;
    }

    try {
      const result = await toggleLike(user.uid, id, action);
      const newRating = result.newType || null;
      setUserRating(newRating);
      
      // Update counts optimistically
      setLikeCounts(prev => {
        const newCounts = { ...prev };
        if (newRating === 'like') {
          newCounts.likes += 1;
          if (userRating === 'dislike') {
            newCounts.dislikes = Math.max(0, newCounts.dislikes - 1);
          }
        } else if (newRating === 'dislike') {
          newCounts.dislikes += 1;
          if (userRating === 'like') {
            newCounts.likes = Math.max(0, newCounts.likes - 1);
          }
        } else {
          // User removed their rating
          if (userRating === 'like') {
            newCounts.likes = Math.max(0, newCounts.likes - 1);
          } else if (userRating === 'dislike') {
            newCounts.dislikes = Math.max(0, newCounts.dislikes - 1);
          }
        }
        return newCounts;
      });

      // Create notification for content creator (if not the creator themselves)
      if (content && content.uploaderId && user.uid !== content.uploaderId && newRating) {
        try {
          const { NotificationService } = await import('../services/notificationService');
          await NotificationService.createNotification(
            content.uploaderId,
            action === 'like' ? 'like' : 'system',
            action === 'like' ? 'New Like' : 'New Dislike',
            `${user.displayName || 'Someone'} ${action}d your content "${content.title}"`,
            `/content/${content.id}`,
            { contentId: content.id, userId: user.uid, action }
          );
        } catch (error) {
          console.error('Error creating notification:', error);
          // Don't show error to user - notification is non-critical
        }
      }

      toast({
        title: newRating ? `${action === 'like' ? 'Liked' : 'Disliked'}!` : 'Rating removed',
        description: newRating ? `You ${action}d this content` : 'Your rating has been removed',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Like error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update rating. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleShare = async () => {
    if (!content) return;
    
    try {
      const shareLink = await generateShareLink(content.id, 'content');
      await shareContent(user?.uid || '', content.id, 'content', 'link');
      
      // Create notification for content creator (if not the creator themselves)
      if (content.uploaderId && user && user.uid !== content.uploaderId) {
        try {
          const { NotificationService } = await import('../services/notificationService');
          await NotificationService.createNotification(
            content.uploaderId,
            'share',
            'Content Shared',
            `${user.displayName || 'Someone'} shared your content "${content.title}"`,
            `/content/${content.id}`,
            { contentId: content.id, userId: user.uid }
          );
        } catch (error) {
          console.error('Error creating notification:', error);
          // Don't show error to user - notification is non-critical
        }
      }
      
      toast({
        title: 'Shared!',
        description: 'Content link copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Share error:', error);
      toast({
        title: 'Error',
        description: 'Failed to share content. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFollow = async () => {
    if (!user || !content) {
      toast({ title: 'Please sign in', status: 'warning' });
      return;
    }

    try {
      if (isSubscribedToCreator) {
        await unfollowCreator(user.uid, content.uploaderId);
        setIsSubscribedToCreator(false);
        setSubscriberCount(prev => Math.max(0, prev - 1));
        toast({
          title: 'Unfollowed',
          description: `You unfollowed ${content.uploader}`,
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
      } else {
        await followCreator(user.uid, content.uploaderId);
        setIsSubscribedToCreator(true);
        setSubscriberCount(prev => prev + 1);
        toast({
          title: 'Following!',
          description: `You are now following ${content.uploader}`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Follow error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update follow status. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDonate = () => {
    if (content?.uploaderId) {
      navigate(`/donation/${content.uploaderId}/${content.id}`, {
        state: { creatorName: content.uploader }
      });
    }
  };

  const handleDeleteContent = async () => {
    if (!user || !content) return;
    
    if (user.uid !== content.uploaderId) {
      toast({
        title: 'Permission Denied',
        description: 'You can only delete your own content.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onDeleteDialogOpen();
  };

  const confirmDeleteContent = async () => {
    if (!user || !content) return;

    setIsDeleting(true);
    setDeleteProgress(0);

    // Show initial progress
    toast({
      title: 'Deleting Content...',
      description: 'Removing content and all associated data. This may take a moment.',
      status: 'info',
      duration: 10000,
      isClosable: true,
    });

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setDeleteProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      const result = await deleteContent(content.id, user.uid);
      
      clearInterval(progressInterval);
      setDeleteProgress(100);
      
      if (result.success) {
        toast({
          title: 'Content Deleted Successfully',
          description: `Deleted ${result.deletedItems.files} files and all associated data.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setTimeout(() => navigate('/creator-dashboard'), 1000);
      } else {
        toast({
          title: 'Delete Failed',
          description: result.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error deleting content:', error);
      toast({
        title: 'Delete Failed',
        description: 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      setDeleteProgress(0);
      onDeleteDialogClose();
    }
  };

  
  const checkDonationAccess = async (): Promise<boolean> => {
    if (!content || !user) return true; // Allow access if no user or no content
    
    // Check if content requires donation
    if (!(content as any).donationRequired) return true;
    
    // Premium users bypass donation requirement
    if (isAdFree) {
      return true;
    }
    
    // Check if user has already donated to this content
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../lib/firebase');
      const donationsQuery = query(
        collection(db, 'donations'),
        where('donorId', '==', user.uid),
        where('contentId', '==', content.id || ''),
        where('status', '==', 'completed')
      );
      const snapshot = await getDocs(donationsQuery);
      if (!snapshot.empty) {
        setHasDonated(true);
        return true;
      }
    } catch (error) {
      console.error('Error checking donation access:', error);
    }
    
    // Show paywall modal
    setIsDonationPaywallOpen(true);
    return false;
  };

  const handlePlay = async () => {
    if (!content) {
      return;
    }

    // Check donation requirement
    const hasAccess = await checkDonationAccess();
    if (!hasAccess) {
      return; // Donation paywall modal will be shown
    }
    
    // For series, play the first episode of the first season
    if (content.category === 'series') {
      // Check if we have seasons data directly in content
      const contentSeasons = (content as any).seasons;
      if (contentSeasons && Array.isArray(contentSeasons) && contentSeasons.length > 0) {
        const firstSeason = contentSeasons[0];
        if (firstSeason.episodes && firstSeason.episodes.length > 0) {
          const firstEpisode = firstSeason.episodes[0];
          
          let videoUrl = '';
          if (firstEpisode.videoUrl) {
            videoUrl = firstEpisode.videoUrl;
          }
          
          if (!videoUrl) {
            toast({
              title: 'No video available',
              description: 'This episode does not have a video file.',
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
            return;
          }
          
          navigate(`/player/${id}-s1-e1`, {
            state: {
              src: videoUrl,
              title: firstEpisode.title,
              subtitles: [],
              creatorId: content.uploaderId,
              contentId: `${id}-s1-e1`,
              uploader: content.uploader
            }
          });
          return;
        }
      }
      
      // If seasons are not loaded yet, try to load them first (but only once)
      if (seasons.length === 0) {
        fetchEpisodes();
        return;
      }
      
      if (seasons.length > 0 && seasons[0].episodes.length > 0) {
      const firstEpisode = seasons[0].episodes[0];
      watchStartRef.current = Date.now();
      
      let videoUrl = '';
      if (firstEpisode.assets) {
        // Priority order: videoFile -> videoUrl -> video -> trailer
        if (firstEpisode.assets['videoFile']) {
          videoUrl = firstEpisode.assets['videoFile'] as string;
        } else if (firstEpisode.assets['videoUrl']) {
          videoUrl = firstEpisode.assets['videoUrl'] as string;
        } else if (firstEpisode.assets['video']) {
          videoUrl = firstEpisode.assets['video'] as string;
        } else if (firstEpisode.assets['trailer']) {
          videoUrl = firstEpisode.assets['trailer'] as string;
        }
      }
      
      // Also check for videoUrl directly on episode (not in assets)
      if (!videoUrl && (firstEpisode as any).videoUrl) {
        videoUrl = (firstEpisode as any).videoUrl;
      }
      
      // Search for any video file in assets
      if (!videoUrl && firstEpisode.assets) {
        for (const [key, value] of Object.entries(firstEpisode.assets)) {
          if (typeof value === 'string' && 
              (value.includes('firebasestorage') || value.includes('http') || value.includes('https')) &&
              (key.toLowerCase().includes('video') || 
               value.toLowerCase().includes('.mp4') || 
               value.toLowerCase().includes('.mov') ||
               value.toLowerCase().includes('.avi'))) {
            videoUrl = value;
            break;
          }
        }
      }
      
      if (!videoUrl) {
        toast({
          title: 'No video available',
          description: 'This episode does not have a video file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      let subtitles: string[] = [];
      if (firstEpisode.assets && firstEpisode.assets['subtitles']) {
        const subtitleData = firstEpisode.assets['subtitles'];
        if (typeof subtitleData === 'string') {
          subtitles = subtitleData.split(',').map(s => s.trim()).filter(s => s);
        } else if (Array.isArray(subtitleData)) {
          subtitles = subtitleData.filter(s => s);
        }
      }
      
      navigate(`/player/${firstEpisode.id}`, {
        state: {
          src: videoUrl,
          title: firstEpisode.title,
          subtitles: subtitles,
          creatorId: firstEpisode.uploaderId,
          contentId: firstEpisode.id,
          uploader: firstEpisode.uploader
        }
      });
      return;
      } else {
        toast({
          title: 'No episodes available',
          description: 'This series does not have any episodes available.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }
    
    // For movies or other content, play the main content
    watchStartRef.current = Date.now();
    let videoUrl = '';
    
    // First, try to find the main video file in assets
    if (content.assets) {
      // Priority 1: Check for explicit video file keys (most common)
      if (content.assets['videoFile']) {
        videoUrl = content.assets['videoFile'] as string;
      } else if (content.assets['videoUrl']) {
        videoUrl = content.assets['videoUrl'] as string;
      } else if (content.assets['video']) {
        videoUrl = content.assets['video'] as string;
      }
      
      // Priority 2: Look for video files (not trailer) - check for common video keys
      if (!videoUrl) {
        const videoKeys = ['main', 'movie', 'content'];
        for (const key of videoKeys) {
          if (content.assets[key]) {
            videoUrl = content.assets[key] as string;
            break;
          }
        }
      }
      
      // Priority 3: If no main video found, check for any video file that's not a trailer
      if (!videoUrl) {
        for (const [key, value] of Object.entries(content.assets)) {
          if (typeof value === 'string' && 
              (value.includes('firebasestorage') || value.includes('http') || value.includes('https')) && 
              !key.toLowerCase().includes('trailer') && 
              (value.toLowerCase().includes('.mp4') || value.toLowerCase().includes('.mov') || value.toLowerCase().includes('.avi') || value.toLowerCase().includes('.webm'))) {
            videoUrl = value;
            break;
          }
        }
      }
      
      // Priority 4: Special check for files that look like main content (not trailer)
      if (!videoUrl) {
        for (const [key, value] of Object.entries(content.assets)) {
          if (typeof value === 'string' && 
              (value.includes('firebasestorage') || value.includes('http') || value.includes('https')) && 
              !key.toLowerCase().includes('trailer') && 
              !key.toLowerCase().includes('poster') &&
              !key.toLowerCase().includes('thumbnail') &&
              (key.includes('tweeload') || key.includes('video') || key.includes('main'))) {
            videoUrl = value;
            break;
          }
        }
      }
      
      // Priority 5: If still no video found, fall back to trailer
      if (!videoUrl && content.assets['trailer']) {
        videoUrl = content.assets['trailer'] as string;
      }
    }
    
    // Final fallback to content.trailer
    if (!videoUrl && content.trailer) {
      videoUrl = content.trailer;
    }
    
    if (!videoUrl) {
      toast({
        title: 'No video available',
        description: 'This content does not have a video file.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    let subtitles: string[] = [];
    if (content.assets && content.assets['subtitles']) {
      const subtitleData = content.assets['subtitles'];
      if (typeof subtitleData === 'string') {
        subtitles = subtitleData.split(',').map(s => s.trim()).filter(s => s);
      } else if (Array.isArray(subtitleData)) {
        subtitles = subtitleData.filter(s => s);
      }
    }
    
    navigate(`/player/${id}`, {
      state: {
        src: videoUrl,
        title: content.title,
        subtitles: subtitles,
        creatorId: content.uploaderId,
        contentId: id,
        uploader: content.uploader
      }
    });
  };

  const handlePlaySeason = (seasonNumber: string) => {
    const selectedSeason = seasons.find(s => s.seasonNumber === seasonNumber);
    if (selectedSeason && selectedSeason.episodes.length > 0) {
      // Play the first episode of the selected season
      const firstEpisode = selectedSeason.episodes[0];
      watchStartRef.current = Date.now();
      
      let videoUrl = '';
      if (firstEpisode.assets) {
        if (firstEpisode.assets['video']) {
          videoUrl = firstEpisode.assets['video'] as string;
        } else if (firstEpisode.assets['videoFile']) {
          videoUrl = firstEpisode.assets['videoFile'] as string;
        } else if (firstEpisode.assets['trailer']) {
          videoUrl = firstEpisode.assets['trailer'] as string;
        }
      }
      
      if (!videoUrl) {
        toast({
          title: 'No video available',
          description: 'This episode does not have a video file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      let subtitles: string[] = [];
      if (firstEpisode.assets && firstEpisode.assets['subtitles']) {
        const subtitleData = firstEpisode.assets['subtitles'];
        if (typeof subtitleData === 'string') {
          subtitles = subtitleData.split(',').map(s => s.trim()).filter(s => s);
        } else if (Array.isArray(subtitleData)) {
          subtitles = subtitleData.filter(s => s);
        }
      }
      
      navigate(`/player/${firstEpisode.id}`, {
        state: {
          src: videoUrl,
          title: firstEpisode.title,
          subtitles: subtitles,
          creatorId: firstEpisode.uploaderId,
          contentId: firstEpisode.id,
          uploader: firstEpisode.uploader
        }
      });
    } else {
      toast({
        title: 'No episodes available',
        description: `No episodes found for Season ${seasonNumber}.`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePlayTrailer = () => {
    if (!content) return;
    
    watchStartRef.current = Date.now();
    let videoUrl = '';
    
    // Only look for trailer files
    if (content.trailer) {
      videoUrl = content.trailer;
    } else if (content.assets && content.assets['trailer']) {
      videoUrl = content.assets['trailer'] as string;
    }
                
                if (!videoUrl) {
                  toast({
        title: 'No trailer available',
        description: 'This content does not have a trailer.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                  });
                  return;
                }
                
                let subtitles: string[] = [];
                if (content.assets && content.assets['subtitles']) {
                  const subtitleData = content.assets['subtitles'];
                  if (typeof subtitleData === 'string') {
                    subtitles = subtitleData.split(',').map(s => s.trim()).filter(s => s);
                  } else if (Array.isArray(subtitleData)) {
                    subtitles = subtitleData.filter(s => s);
                  }
                }
                
                navigate(`/player/${id}`, {
                  state: {
                    src: videoUrl,
        title: `${content.title} Trailer`,
                    subtitles: subtitles,
                    creatorId: content.uploaderId,
                    contentId: id,
                    uploader: content.uploader
                  }
                });
  };

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="purple.400" />
      </Center>
    );
  }

  if (error || !content) {
  return (
    <Box minH="100vh" bg="white" _dark={{ bg: "gray.900" }} w="100%" maxW="100%" overflowX="hidden">
        {/* Header with Back Button */}
        <Flex
          position="sticky"
          top={0}
          zIndex={10}
          bg="white"
          _dark={{ bg: "gray.900" }}
          p={4}
          align="center"
          gap={4}
          borderBottom="1px solid"
          borderColor={{ base: "gray.200", _dark: "gray.700" }}
        >
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
          />
          <Heading size="md" color="gray.900" _dark={{ color: "white" }}>
            Content Not Found
          </Heading>
        </Flex>
        
        <Container maxW="container.sm" px={4} py={8}>
          <VStack gap={4} textAlign="center">
            <Text color="red.400" fontSize="lg" fontWeight="bold">
              {error || 'Content not found'}
            </Text>
            <Text color="gray.500" fontSize="md">
              The content you're looking for doesn't exist or you don't have permission to view it.
            </Text>
            <Text color="gray.400" fontSize="sm">
              Content ID: {id}
            </Text>
            <Button
              colorScheme="purple"
              onClick={() => navigate('/')}
            >
              Go Home
            </Button>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="white" _dark={{ bg: "gray.900" }} w="100%" maxW="100%" overflowX="hidden">
      {/* Header with Back Button and Title */}
      <Flex
        position="sticky"
        top={0}
        zIndex={10}
        bg="white"
        _dark={{ bg: "gray.900" }}
        p={4}
        align="center"
        gap={4}
        borderBottom="1px solid"
        borderColor={{ base: "gray.200", _dark: "gray.700" }}
        w="100%"
        maxW="100%"
        overflowX="hidden"
      >
        <IconButton
          aria-label="Go back"
          icon={<IconWrapper icon={FiArrowLeft} size={20} />}
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
        />
        <Heading size="md" color="gray.900" _dark={{ color: "white" }}>
          {content.title}
        </Heading>
      </Flex>

      {/* Main Content Layout */}
      <Container maxW="container.xl" px={4} py={{ base: 4, lg: 8 }} w="100%" overflowX="hidden">
        <AdSenseSlot
          client={ADSENSE_CLIENT_ID}
          slot={ADSENSE_SLOTS.CONTENT_DETAIL_SIDEBAR}
          format="auto"
          fullWidthResponsive
          style={{ margin: '8px 0' }}
        />
        <Flex
          direction={{ base: "column", lg: "row" }}
          gap={{ base: 4, lg: 8 }}
          align="flex-start"
          w="100%"
          maxW="100%"
          overflowX="hidden"
        >
          {/* Left Side - Poster */}
          <Box
            flex={{ base: "0 0 auto", lg: "0 0 300px" }}
            w={{ base: "100%", lg: "300px" }}
            position={{ base: "static", lg: "sticky" }}
            top={{ base: "auto", lg: "100px" }}
            maxW="100%"
            minW="0"
            mb={{ base: 2, lg: 0 }}
          >
            <Box
              w="100%"
              h={{ base: "300px", lg: "450px" }}
              borderRadius="lg"
              overflow="hidden"
              boxShadow="xl"
              bg="gray.100"
              _dark={{ bg: "gray.800" }}
            >
              <CachedImage
                src={
                  content.poster || 
                  content.thumbnail || 
                  (content.assets && content.assets.poster ? 
                    (Array.isArray(content.assets.poster) ? content.assets.poster[0] : content.assets.poster) : '') ||
                  ((content as any).category === 'short' && content.assets && content.assets.videoFile ? 
                    (Array.isArray(content.assets.videoFile) ? content.assets.videoFile[0] : content.assets.videoFile as string) : '') ||
                  ''
                }
                alt={content.title}
                width="100%"
                height="100%"
                objectFit="cover"
                fallbackType={(content as any).category === 'short' ? 'video' : 'movie'}
                size="w500"
              />
            </Box>
          </Box>

          {/* Right Side - Content Details */}
          <Box flex="1" minW="0" w="100%" maxW="100%" overflowX="hidden">
            {/* Content Header */}
            <VStack align="stretch" gap={{ base: 4, lg: 6 }} w="100%" maxW="100%" overflowX="hidden">
              {/* Title and Basic Info */}
              <Box w="100%" maxW="100%" overflowX="hidden">
                <Heading size="2xl" mb={2} color="gray.900" _dark={{ color: "white" }} wordBreak="break-word">
                  {content.title}
                </Heading>
                <HStack gap={4} mb={4} flexWrap="wrap" w="100%" maxW="100%" overflowX="hidden">
                  <Text color="gray.600" _dark={{ color: "gray.300" }} fontSize="lg">
                    {content.year || 'N/A'}
                  </Text>
                  <Badge colorScheme="red" px={2} py={1} borderRadius="md">
                    {content.contentRating || '18+'}
                  </Badge>
                  <Text color="gray.600" _dark={{ color: "gray.300" }} fontSize="lg">
                    {content.language || 'English'}
                  </Text>
                  {content.genres && content.genres.length > 0 && (
                    <Text color="gray.600" _dark={{ color: "gray.300" }} fontSize="lg">
                      {content.genres.slice(0, 3).join(' | ')}
                    </Text>
                  )}
                </HStack>
                
              </Box>

              {/* Description */}
              <Box>
                <Text color="gray.700" _dark={{ color: "gray.300" }} lineHeight="1.6" fontSize="md">
                  {content.description}
                </Text>
              </Box>

              {/* Action Buttons */}
              <HStack gap={4} flexWrap="wrap" w="100%">
                {user ? (
                  <Button
                    colorScheme="purple"
                    size={{ base: 'md', md: 'lg' }}
                    leftIcon={<IconWrapper icon={FiPlay} size={playIconSize} />}
                    onClick={handlePlay}
                    px={{ base: 6, md: 8 }}
                    w={{ base: '100%', md: 'auto' }}
                    minH={{ base: '44px', md: 'auto' }}
                  >
                    Watch Online
                  </Button>
                ) : (
                  <Button
                    colorScheme="purple"
                    size={{ base: 'md', md: 'lg' }}
                    leftIcon={<IconWrapper icon={FiPlay} size={playIconSize} />}
                    onClick={() => navigate('/signup')}
                    px={{ base: 6, md: 8 }}
                    w={{ base: '100%', md: 'auto' }}
                    minH={{ base: '44px', md: 'auto' }}
                  >
                    Join Now to Watch
                  </Button>
                )}
              </HStack>


              {/* Creator Info */}
              <Box p={4} bg="gray.50" _dark={{ bg: "gray.800" }} borderRadius="lg">
                <HStack justify="space-between" align="center">
                  <HStack gap={3}>
                    <Avatar size="md" name={content.uploader} src={creatorProfile?.avatarUrl} />
                    <VStack align="start" gap={0}>
                      <Text fontWeight="bold" fontSize="md">{content.uploader}</Text>
                      <Text color="gray.500" fontSize="sm">
                        {subscriberCount >= 1000 ? `${(subscriberCount / 1000).toFixed(1)}k` : subscriberCount} followers
                      </Text>
                    </VStack>
                  </HStack>
                  {user?.uid !== content.uploaderId && (
                    <Button
                      colorScheme="purple"
                      size="sm"
                      onClick={handleFollow}
                      variant={isSubscribedToCreator ? "outline" : "solid"}
                    >
                      {isSubscribedToCreator ? "Following" : "Follow"}
                    </Button>
                  )}
                </HStack>
              </Box>

              {/* User Interaction Buttons */}
              <HStack justify="space-around" color="gray.500" flexWrap="wrap" gap={4} w="100%" maxW="100%" overflowX="hidden">
                <VStack 
                  gap={1} 
                  cursor="pointer" 
                  onClick={handleBookmark}
                  p={3}
                  borderRadius="md"
                  _hover={{ bg: 'gray.50', _dark: { bg: 'gray.700' } }}
                  transition="all 0.2s"
                  minW="80px"
                >
                  <IconWrapper 
                    icon={isBookmarked ? FaBookmarkSolid : FiBookmark} 
                    size={20} 
                    color={isBookmarked ? 'purple.500' : 'gray.500'} 
                  />
                  <Text fontSize="xs" color={isBookmarked ? 'purple.500' : 'gray.500'} fontWeight={isBookmarked ? 'bold' : 'normal'}>
                    {isBookmarked ? 'Saved' : 'Watchlist'}
                  </Text>
                </VStack>
                <VStack 
                  gap={1} 
                  cursor="pointer" 
                  onClick={() => handleLike('like')}
                  p={3}
                  borderRadius="md"
                  _hover={{ bg: 'gray.50', _dark: { bg: 'gray.700' } }}
                  transition="all 0.2s"
                  minW="80px"
                >
                  <IconWrapper 
                    icon={userRating === 'like' ? FaThumbsUp : FiThumbsUp} 
                    size={20} 
                    color={userRating === 'like' ? 'green.500' : 'gray.500'} 
                  />
                  <Text fontSize="xs" color={userRating === 'like' ? 'green.500' : 'gray.500'} fontWeight={userRating === 'like' ? 'bold' : 'normal'}>
                    Like
                  </Text>
                </VStack>
                <VStack 
                  gap={1} 
                  cursor="pointer" 
                  onClick={() => handleLike('dislike')}
                  p={3}
                  borderRadius="md"
                  _hover={{ bg: 'gray.50', _dark: { bg: 'gray.700' } }}
                  transition="all 0.2s"
                  minW="80px"
                >
                  <IconWrapper 
                    icon={userRating === 'dislike' ? FaThumbsDown : FiThumbsDown} 
                    size={20} 
                    color={userRating === 'dislike' ? 'red.500' : 'gray.500'} 
                  />
                  <Text fontSize="xs" color={userRating === 'dislike' ? 'red.500' : 'gray.500'} fontWeight={userRating === 'dislike' ? 'bold' : 'normal'}>
                    Dislike
                  </Text>
                </VStack>
                <VStack 
                  gap={1} 
                  cursor="pointer" 
                  onClick={handleShare}
                  p={3}
                  borderRadius="md"
                  _hover={{ bg: 'gray.50', _dark: { bg: 'gray.700' } }}
                  transition="all 0.2s"
                  minW="80px"
                >
                  <IconWrapper icon={FiShare} size={20} />
                  <Text fontSize="xs">Share</Text>
                </VStack>
                <VStack 
                  gap={1} 
                  cursor="pointer" 
                  onClick={handleDonate}
                  p={3}
                  borderRadius="md"
                  _hover={{ bg: 'gray.50', _dark: { bg: 'gray.700' } }}
                  transition="all 0.2s"
                  minW="80px"
                >
                  <IconWrapper icon={FiHeart} size={20} />
                  <Text fontSize="xs">Donate</Text>
                </VStack>
                {user && content && user.uid !== content.uploaderId && (
                  <VStack 
                    gap={1} 
                    cursor="pointer" 
                    onClick={() => setIsReportContentModalOpen(true)}
                    p={3}
                    borderRadius="md"
                    _hover={{ bg: 'gray.50', _dark: { bg: 'gray.700' } }}
                    transition="all 0.2s"
                    minW="80px"
                  >
                    <IconWrapper icon={FiFlag} size={20} />
                    <Text fontSize="xs">Report</Text>
                  </VStack>
                )}
              </HStack>

              {/* Content Owner Actions */}
              {user && content && user.uid === content.uploaderId && (
                <Box p={4} bg="red.50" _dark={{ bg: "red.900", borderColor: "red.700" }} borderRadius="lg" border="1px solid" borderColor="red.200">
                  <VStack gap={3}>
                    <Text fontSize="sm" color="red.600" _dark={{ color: "red.300" }} fontWeight="medium">
                      Content Management
                    </Text>
                    <HStack gap={3} w="100%">
                      <Button
                        variant="outline"
                        colorScheme="blue"
                        size="md"
                        flex="1"
                        onClick={() => {
                          if (content.category === 'short') {
                            navigate(`/short/${content.id}/edit`);
                          } else if (content.category === 'comic') {
                            navigate(`/comic/${content.id}/edit`);
                          } else if (content.category === 'book') {
                            navigate(`/book/${content.id}/edit`);
                          } else if (content.category === 'series') {
                            navigate(`/series/${content.id}/edit`);
                          } else if (content.category === 'movie') {
                            navigate(`/movie/${content.id}/edit`);
                          } else {
                            setIsEditModalOpen(true);
                          }
                        }}
                      >
                        Edit Content
                      </Button>
                      <Button
                        variant="outline"
                        colorScheme="red"
                        size="md"
                        flex="1"
                        onClick={handleDeleteContent}
                        isLoading={isDeleting}
                        loadingText="Deleting..."
                        isDisabled={isDeleting}
                      >
                        Delete Forever
                      </Button>
                    </HStack>
                    <Text fontSize="xs" color="red.500" textAlign="center">
                      Delete: Permanently remove all data and files
                    </Text>
                    {isDeleting && (
                      <VStack gap={2} w="100%" mt={3}>
                        <Text fontSize="sm" color="red.600" textAlign="center">
                          Deleting content and files... {Math.round(deleteProgress)}%
                        </Text>
                        <Progress 
                          value={deleteProgress} 
                          size="sm" 
                          colorScheme="red" 
                          w="100%" 
                          borderRadius="full"
                        />
                      </VStack>
                    )}
                  </VStack>
                </Box>
              )}
            </VStack>
          </Box>
        </Flex>
      </Container>

      {/* Content Details with Tabs */}
      <Container maxW="container.xl" px={4} pb={{ base: "100px", md: 8 }} w="100%" overflowX="hidden">
        <Tabs variant="line" colorScheme="purple" mb={6} w="100%" maxW="100%" overflowX="hidden">
          <TabList overflowX="auto" css={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }} w="100%" maxW="100%">
            <Tab minW="fit-content" px={2} maxW="100%">
              <HStack gap={2} minW="0">
                <IconWrapper icon={FiInfo} size={16} />
                <Text fontSize="sm" wordBreak="break-word">Info</Text>
              </HStack>
            </Tab>
            {content.category !== 'short' && (
              <Tab minW="fit-content" px={2} maxW="100%">
                <HStack gap={2} minW="0">
                  <IconWrapper icon={FiPlay} size={16} />
                  <Text fontSize="sm" wordBreak="break-word">Trailer</Text>
                </HStack>
              </Tab>
            )}
            {content.category === 'series' && (
              <Tab minW="fit-content" px={2} maxW="100%">
                <HStack gap={2} minW="0">
                  <IconWrapper icon={FiList} size={16} />
                  <Text fontSize="sm" wordBreak="break-word">Seasons</Text>
                </HStack>
              </Tab>
            )}
            {content.category === 'movie' && (
              <Tab minW="fit-content" px={2} maxW="100%">
                <HStack gap={2} minW="0">
                  <IconWrapper icon={FiPlay} size={16} />
                  <Text fontSize="sm" wordBreak="break-word">Movie</Text>
                </HStack>
              </Tab>
            )}
            {content.category === 'short' && (
              <Tab minW="fit-content" px={2} maxW="100%">
                <HStack gap={2} minW="0">
                  <IconWrapper icon={FiPlay} size={16} />
                  <Text fontSize="sm" wordBreak="break-word">Video</Text>
                </HStack>
              </Tab>
            )}
          </TabList>

          <TabPanels>
            {/* Info Tab */}
            <TabPanel px={0} w="100%" maxW="100%" overflowX="hidden">
              <VStack align="stretch" gap={4} w="100%" maxW="100%" overflowX="hidden">
                <Heading size="md" mb={2} wordBreak="break-word">{content.title}</Heading>
                
                <Text color="gray.600" _dark={{ color: "gray.300" }} lineHeight="1.6" wordBreak="break-word">
                  {content.description}
                </Text>

                {/* Cast - Only show for movies/series, not shorts */}
                {content.category !== 'short' && content.cast && content.cast.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Cast:</Text>
                    <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
                      {content.cast.join(', ')}
                    </Text>
                  </Box>
                )}

                {/* Director - Only show for movies/series, not shorts */}
                {content.category !== 'short' && content.director && content.director.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Director:</Text>
                    <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
                      {content.director.join(', ')}
                    </Text>
                  </Box>
                )}

                {/* Writer - Only show for movies/series, not shorts */}
                {content.category !== 'short' && content.writer && content.writer.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Writer:</Text>
                    <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
                      {content.writer.join(', ')}
                    </Text>
                  </Box>
                )}

                {/* Metadata Table */}
                <SimpleGrid columns={3} gap={4} p={4} bg="gray.50" _dark={{ bg: "gray.800" }} borderRadius="lg" w="100%" maxW="100%" overflowX="hidden">
                      <VStack gap={1}>
                    <Text fontSize="xs" color="gray.500">
                      {content.category === 'short' ? 'Rating' : 'IMDb'}
                    </Text>
                    <Text fontWeight="bold">
                      {content.category === 'short' 
                        ? (content.contentRating || 'All Ages')
                        : (typeof content.rating === 'number' ? content.rating.toFixed(1) : content.rating || 'N/A')
                      }
                    </Text>
                      </VStack>
                      <VStack gap={1}>
                    <Text fontSize="xs" color="gray.500">Duration</Text>
                    <Text fontWeight="bold">
                      {content.duration ? (
                        content.duration < 60 
                          ? `${content.duration}s` 
                          : `${Math.round(content.duration / 60)}min`
                      ) : 'N/A'}
                    </Text>
                      </VStack>
                      <VStack gap={1}>
                    <Text fontSize="xs" color="gray.500">
                      {content.category === 'short' ? 'Uploaded' : 'Release year'}
                    </Text>
                    <Text fontWeight="bold">
                      {content.category === 'short' 
                        ? (content.createdAt ? 
                            (typeof content.createdAt === 'object' && 'seconds' in content.createdAt 
                              ? new Date((content.createdAt as any).seconds * 1000).getFullYear()
                              : new Date(content.createdAt).getFullYear()
                            ) : 'N/A')
                        : (content.year || 'N/A')
                      }
                    </Text>
                      </VStack>
                </SimpleGrid>

                {/* Genre Tags */}
                {content.genres && content.genres.length > 0 && (
                  <Box w="100%" maxW="100%" overflowX="hidden">
                    <Text fontWeight="bold" mb={2}>Genre:</Text>
                    <Flex wrap="wrap" gap={2} w="100%" maxW="100%" overflowX="hidden">
                      {content.genres.map((genre, index) => (
                        <Badge key={index} colorScheme="gray" variant="subtle" px={2} py={1} borderRadius="full" maxW="100%" wordBreak="break-word">
                          {genre}
                        </Badge>
                      ))}
                    </Flex>
                  </Box>
                )}

                {/* Tags */}
                {content.tags && (
                  <Box w="100%" maxW="100%" overflowX="hidden">
                    <Text fontWeight="bold" mb={2}>Tags:</Text>
                    <Flex wrap="wrap" gap={2} w="100%" maxW="100%" overflowX="hidden">
                      {(Array.isArray(content.tags) ? content.tags : content.tags.split(',').map(t => t.trim())).map((tag, index) => (
                        <Badge key={index} colorScheme="blue" variant="subtle" px={2} py={1} borderRadius="full" maxW="100%" wordBreak="break-word">
                          {tag}
                        </Badge>
                      ))}
                    </Flex>
                  </Box>
                )}

                {/* Language Tags */}
                <Box w="100%" maxW="100%" overflowX="hidden">
                  <Text fontWeight="bold" mb={2}>Languages:</Text>
                  <Flex wrap="wrap" gap={2} w="100%" maxW="100%" overflowX="hidden">
                    <Badge colorScheme="gray" variant="subtle" px={2} py={1} borderRadius="full" maxW="100%" wordBreak="break-word">
                      {content.language || 'English'}
                    </Badge>
                  </Flex>
                </Box>

                {/* Subtitle Tags - Only show if subtitles exist */}
                {content.assets && content.assets.subtitles && (
                  <Box w="100%" maxW="100%" overflowX="hidden">
                    <Text fontWeight="bold" mb={2}>Subtitle:</Text>
                    <Flex wrap="wrap" gap={2} w="100%" maxW="100%" overflowX="hidden">
                      <Badge colorScheme="gray" variant="subtle" px={2} py={1} borderRadius="full" maxW="100%" wordBreak="break-word">
                        {content.language || 'English'}
                      </Badge>
                    </Flex>
                  </Box>
                )}

                {/* Quality Badges - Only show content rating from Firebase */}
                <Flex wrap="wrap" gap={2} w="100%" maxW="100%" overflowX="hidden">
                  <Badge colorScheme="purple" px={2} py={1} borderRadius="full" maxW="100%" wordBreak="break-word">
                    {content.contentRating || content.rating || 'All Ages'}
                  </Badge>
                </Flex>
            </VStack>
          </TabPanel>
          
                {/* Video/Trailer Tab */}
          <TabPanel px={0}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" mb={4}>
                  {content.category === 'short' ? 'Video' : 'Trailer'}
                </Heading>
                {(content.trailer || (content.category === 'short' && content.assets && content.assets.videoFile)) ? (
              <Box
                w="100%"
                    h="200px"
                        bg="gray.100"
                        _dark={{ bg: "gray.700" }}
                          borderRadius="lg"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    border="2px dashed"
                    borderColor={{ base: "gray.300", _dark: "gray.600" }}
                  >
                    <VStack gap={2}>
                      <Text color="gray.500" fontSize="lg">
                        {content.category === 'short' ? 'Video Available' : 'Trailer Available'}
                      </Text>
                      <Button
                        colorScheme="purple"
                        leftIcon={<IconWrapper icon={FiPlay} size={16} />}
                        onClick={content.category === 'short' ? handlePlay : handlePlayTrailer}
                      >
                        {content.category === 'short' ? 'Play Video' : 'Play Trailer'}
                      </Button>
                    </VStack>
              </Box>
            ) : (
              <Box
                w="100%"
                    h="200px"
                bg="gray.100"
                _dark={{ bg: "gray.700" }}
                    borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                        border="2px dashed"
                    borderColor={{ base: "gray.300", _dark: "gray.600" }}
              >
                        <VStack gap={2}>
                          <Text color="gray.500" fontSize="lg">
                    {content.category === 'short' ? 'No video available' : 'No trailer available'}
                  </Text>
                          <Text color="gray.400" fontSize="sm">
                            {content.category === 'short' ? 'Video will appear here when uploaded' : 'Trailer will appear here when uploaded'}
                  </Text>
                </VStack>
              </Box>
                )}
              </VStack>
          </TabPanel>
          
          {/* Movie Tab - Only for movies */}
          {content.category === 'movie' && (
            <TabPanel px={0}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" mb={4}>{content.title} - Movie</Heading>
                <Box
                  w="100%"
                  h="200px"
                  bg="gray.100"
                  _dark={{ bg: "gray.700" }}
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="2px dashed"
                  borderColor={{ base: "gray.300", _dark: "gray.600" }}
                  cursor="pointer"
                  onClick={handlePlay}
                  _hover={{ bg: "gray.200", _dark: { bg: "gray.600" } }}
                  transition="all 0.2s"
                >
                  <VStack gap={2}>
                    <CachedImage
                      src={
                        content.poster || 
                        content.thumbnail || 
                        (content.assets && content.assets.poster ? 
                          (Array.isArray(content.assets.poster) ? content.assets.poster[0] : content.assets.poster) : '') ||
                        ((content as any).category === 'short' && content.assets && content.assets.videoFile ? 
                          (Array.isArray(content.assets.videoFile) ? content.assets.videoFile[0] : content.assets.videoFile as string) : '') ||
                        ''
                      }
                      alt={content.title}
                      width="120px"
                      height="80px"
                      objectFit="cover"
                      borderRadius="md"
                      fallbackType={(content as any).category === 'short' ? 'video' : 'movie'}
                      size="w300"
                    />
                    <HStack gap={2}>
                      <Text color="gray.700" _dark={{ color: "gray.300" }} fontSize="lg" fontWeight="bold">
                        {content.title}
                      </Text>
                      <IconWrapper icon={FiPlay} size={20} color="purple.400" />
                    </HStack>
                    <Text color="gray.500" fontSize="sm">
                      {content.duration ? (
                        content.duration < 60 
                          ? `${content.duration}s` 
                          : `${Math.round(content.duration / 60)}min`
                      ) : 'Duration not available'}
                    </Text>
                  </VStack>
                </Box>
              </VStack>
            </TabPanel>
          )}
          
                {/* Seasons Tab - Only for series */}
          {content.category === 'series' && (
            <TabPanel px={0}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" mb={4}>Seasons</Heading>
                {loadingSeasons ? (
                  <Center>
                    <Spinner size="md" color="purple.400" />
                  </Center>
                ) : seasons.length > 0 ? (
                  seasons.map(season => (
                    <Box
                      key={season.seasonNumber}
                      p={4}
                      bg="gray.50"
                      _dark={{ bg: "gray.700" }}
                      borderRadius="md"
                      cursor="pointer"
                      onClick={() => handlePlaySeason(season.seasonNumber)}
                      _hover={{ bg: "gray.100", _dark: { bg: "gray.600" } }}
                      transition="all 0.2s"
                    >
                      <HStack justify="space-between" align="center">
                        <VStack align="flex-start" gap={1}>
                          <Text fontWeight="bold" fontSize="lg">
                            Season {season.seasonNumber}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {season.episodeCount} {season.episodeCount === 1 ? 'episode' : 'episodes'}
                          </Text>
                        </VStack>
                        <IconWrapper icon={FiPlay} size={20} color="purple.400" />
                      </HStack>
                    </Box>
                  ))
                ) : (
                  <Box
                    w="100%"
                    h="200px"
                    bg="gray.100"
                    _dark={{ bg: "gray.700" }}
                    borderRadius="lg"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    border="2px dashed"
                    borderColor={{ base: "gray.300", _dark: "gray.600" }}
                  >
                    <VStack gap={2}>
                      <Text color="gray.500" fontSize="lg">
                        No seasons available
                      </Text>
                      <Text color="gray.400" fontSize="sm">
                        Seasons will appear here when added
                      </Text>
                    </VStack>
                  </Box>
                )}
                  </VStack>
            </TabPanel>
          )}
          
        </TabPanels>
        </Tabs>
      </Container>

      {/* Comments Section */}
      {content && (
        <Container maxW="container.xl" py={8} pb={{ base: "100px", md: 8 }} w="100%" overflowX="hidden">
          <CommentSection 
            contentId={content.id} 
            contentCreatorId={content.uploaderId}
            contentTitle={content.title}
          />
        </Container>
      )}
      

        {/* Content-Specific Edit Modals */}
        {content && user && (
          <>
            {content.category === 'book' && (
              <EditBookModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                contentId={content.id}
                userId={user.uid}
                onSuccess={() => {
                  window.location.reload();
                }}
              />
            )}
            {/* Fallback to generic modal for other content types */}
            {!['movie', 'series', 'comic', 'novel', 'short'].includes(content.category) && (
              <EditContentModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                contentId={content.id}
                userId={user.uid}
                onSuccess={() => {
                  window.location.reload();
                }}
              />
            )}
          </>
        )}

        {/* Episode Edit Modal */}
        <EditEpisodeModal
          isOpen={isEpisodeEditModalOpen}
          onClose={() => {
            setIsEpisodeEditModalOpen(false);
            setSelectedEpisode(null);
          }}
          episode={selectedEpisode}
          contentId={content?.id || ''}
          userId={user?.uid || ''}
          onSuccess={() => {
            // Reload episodes to show updated data
            if (content?.category === 'series') {
              fetchEpisodes();
            }
          }}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          isOpen={isDeleteDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteDialogClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Content Permanently
              </AlertDialogHeader>
              <AlertDialogBody>
                Are you sure you want to delete "{content?.title}"? This action cannot be undone and will permanently remove:
                <br />
                <br />
                • All content files and media
                <br />
                • All comments and interactions
                <br />
                • All analytics and view data
                <br />
                • All associated metadata
                <br />
                <br />
                <Text fontWeight="bold" color="red.500">
                  This action is irreversible!
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteDialogClose}>
                  Cancel
                </Button>
                <Button 
                  colorScheme="red" 
                  onClick={confirmDeleteContent} 
                  ml={3}
                  isLoading={isDeleting}
                  loadingText="Deleting..."
                >
                  Delete Forever
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Report Modals */}
        {content && user && (
          <>
            <ReportContentModal
              isOpen={isReportContentModalOpen}
              onClose={() => setIsReportContentModalOpen(false)}
              contentId={content.id}
              contentType={content.category as any}
              contentTitle={content.title}
              contentCreatorId={content.uploaderId}
              contentCreatorName={content.uploader}
            />
            <ReportUserModal
              isOpen={isReportUserModalOpen}
              onClose={() => setIsReportUserModalOpen(false)}
              reportedUserId={content.uploaderId}
              reportedUserName={content.uploader}
            />
            <DMCARequestModal
              isOpen={isDMCARequestModalOpen}
              onClose={() => setIsDMCARequestModalOpen(false)}
              contentId={content.id}
              contentTitle={content.title}
              contentCreatorId={content.uploaderId}
              contentCreatorName={content.uploader}
            />
            <DonationPaywallModal
              isOpen={isDonationPaywallOpen}
              onClose={() => setIsDonationPaywallOpen(false)}
              contentId={content.id}
              contentTitle={content.title}
              creatorId={content.uploaderId}
              creatorName={content.uploader}
              onDonationComplete={() => {
                setHasDonated(true);
                handlePlay(); // Retry playing after donation
              }}
            />
          </>
        )}
    </Box>
  );
};

export default ContentDetail;